import React, { useCallback, useEffect, useId, useState } from "react";
import { SubscriptionActionFooter } from "./SubscriptionActionDialog";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import Note from "../../library/Note";
import * as adminSubscriptionServices from "../../../contexts/services/admin/adminSubscriptionServices";
import { formatMoney } from "./subscriptionPricing";

const ROLES = [
  { value: "viewer", label: "View only" },
  { value: "editor", label: "Can edit" },
  { value: "admin", label: "Administrator" },
];

const MEMBER_STATUSES = new Set(["active", "trialing", "past_due"]);

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const STATUS_LABELS = {
  active: "Active",
  pending: "Invited",
  banned: "Blocked",
  inactive: "Inactive",
  disabled: "Disabled",
};

const MemberRow = ({ member, owner = false, action }) => (
  <TableRow>
    <TableCell component='th' scope='row' sx={{ overflowWrap: "anywhere" }}>
      <Typography variant='body2' fontWeight={600}>
        {member.name || "Name not provided"}
      </Typography>
      <Typography variant='caption' color='text.secondary'>
        {member.email || "Email not provided"}
      </Typography>
      {owner && (
        <Typography variant='caption' color='primary.main' display='block'>
          Subscription owner
        </Typography>
      )}
    </TableCell>
    <TableCell data-label='Access'>
      {ROLES.find((option) => option.value === member.user_role)?.label ||
        member.user_role ||
        "—"}
    </TableCell>
    <TableCell data-label='Status'>
      <Chip
        size='small'
        variant='outlined'
        label={STATUS_LABELS[member.status] || member.status || "—"}
      />
    </TableCell>
    <TableCell data-label='Billing'>
      {member.billable ? "Included" : "No charge"}
    </TableCell>
    <TableCell data-label='Action' align='right'>
      {owner ? (
        <Typography variant='caption' color='text.secondary'>
          Cannot remove
        </Typography>
      ) : (
        action
      )}
    </TableCell>
  </TableRow>
);

export default function SubscriptionSubUsersAction({
  subscriptionId,
  status,
  updating,
  runActionWithMiddleware,
  setUpdating,
  onComplete,
  onError,
}) {
  const formId = useId();
  const [members, setMembers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const [reason, setReason] = useState(
    "Admin updated subscription users at the customer's request."
  );

  const loadMembers = useCallback(async () => {
    if (!subscriptionId) return;
    setLoading(true);
    setLoadError("");
    try {
      const body =
        await adminSubscriptionServices.getSubscriptionMembers(subscriptionId);
      setMembers(body?.data || null);
    } catch (error) {
      setLoadError(errorMessage(error, "Could not load sub-users"));
    } finally {
      setLoading(false);
    }
  }, [subscriptionId]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const allowed = MEMBER_STATUSES.has(status);
  const reasonReady = Boolean(reason.trim());
  const openRenewal = members?.open_renewals?.[0] || null;
  const changesDisabled = updating || loading || Boolean(loadError) || !members;
  const subUsers = members?.sub_users || [];

  const addSubUser = () => {
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedReason = reason.trim();
    if (!trimmedName || !trimmedEmail || !trimmedReason) {
      onError("Name, email, and reason are required.");
      return;
    }

    runActionWithMiddleware({
      actionKey: "manageSubscriptionSubUsers",
      details: (
        <Typography variant='body2' color='text.secondary'>
          Invite {trimmedEmail}. Access:{" "}
          {ROLES.find((option) => option.value === role)?.label}. Any unpaid
          renewal bill will update to include this user.
        </Typography>
      ),
      execute: async () => {
        setUpdating(true);
        try {
          const body = await adminSubscriptionServices.addSubscriptionSubUser(
            subscriptionId,
            {
              name: trimmedName,
              email: trimmedEmail,
              role,
              reason: trimmedReason,
            }
          );
          setMembers(body?.data?.members || null);
          setName("");
          setEmail("");
          await onComplete(body?.message || "Sub-user saved");
        } catch (error) {
          onError(errorMessage(error, "Could not add the sub-user"));
          await loadMembers();
        } finally {
          setUpdating(false);
        }
      },
    });
  };

  const removeSubUser = (member) => {
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      onError("Reason is required.");
      return;
    }

    runActionWithMiddleware({
      actionKey: "manageSubscriptionSubUsers",
      details: (
        <Typography variant='body2' color='text.secondary'>
          Remove {member.email}? Their user account will be deleted. Any unpaid
          renewal bill will update to exclude this user.
        </Typography>
      ),
      execute: async () => {
        setUpdating(true);
        try {
          const body =
            await adminSubscriptionServices.removeSubscriptionSubUser(
              subscriptionId,
              member.u_id,
              { reason: trimmedReason }
            );
          setMembers(body?.data?.members || null);
          await onComplete(body?.message || "Sub-user removed");
        } catch (error) {
          onError(errorMessage(error, "Could not remove the sub-user"));
          await loadMembers();
        } finally {
          setUpdating(false);
        }
      },
    });
  };

  if (!allowed) {
    return (
      <Note title='Users cannot be changed right now'>
        <Typography variant='body2' color='text.secondary'>
          You can add or remove users when the subscription is active, on a free
          trial, or has an overdue payment.
        </Typography>
      </Note>
    );
  }

  return (
    <Stack
      spacing={2.5}
      sx={{
        minWidth: 0,
        containerType: "inline-size",
        containerName: "sub-users",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr)",
          gap: 3,
          alignItems: "start",
          "@container sub-users (min-width: 1000px)": {
            gridTemplateColumns: "minmax(0, 1fr) 300px",
          },
        }}
      >
        <Stack
          component='section'
          aria-labelledby={`${formId}-users`}
          spacing={1.5}
          sx={{
            minWidth: 0,
            minHeight: 0,
            height: "clamp(520px, 65dvh, 680px)",
            "& > :not(.MuiTableContainer-root)": { flexShrink: 0 },
          }}
        >
          <Typography
            id={`${formId}-users`}
            component='h3'
            variant='subtitle2'
            fontWeight={700}
          >
            Current users
            {members ? ` (${subUsers.length + (members.owner ? 1 : 0)})` : ""}
          </Typography>
          {loading && (
            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
              role='status'
            >
              <CircularProgress size={16} />
              <Typography variant='body2'>Loading users…</Typography>
            </Stack>
          )}
          {loadError && (
            <Note title='Could not load users' error>
              <Typography variant='body2' color='text.secondary'>
                {loadError}
              </Typography>
              <Button
                size='small'
                onClick={loadMembers}
                disabled={loading || updating}
                sx={{ mt: 1 }}
              >
                Try again
              </Button>
            </Note>
          )}
          {members && (
            <>
              <TableContainer
                tabIndex={0}
                role='region'
                aria-label='Current users table'
                sx={{
                  flex: 1,
                  minHeight: 0,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 2,
                  overflow: "auto",
                  scrollbarGutter: "stable",
                  "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: "primary.main",
                    outlineOffset: -2,
                  },
                }}
              >
                <Table
                  stickyHeader
                  size='small'
                  aria-label='Subscription users'
                  sx={{
                    width: "100%",
                    tableLayout: "fixed",
                    "& td, & tbody th": {
                      px: 1.5,
                      py: 1.5,
                      overflowWrap: "anywhere",
                    },
                    "& thead th": {
                      px: 1.5,
                      py: 1.25,
                      fontWeight: 600,
                      bgcolor: "background.default",
                    },
                    "& tbody tr:last-child > *": { borderBottom: 0 },
                    "& thead th:first-of-type": { borderTopLeftRadius: 8 },
                    "& thead th:last-of-type": { borderTopRightRadius: 8 },
                    "@container sub-users (max-width: 639px)": {
                      "&, & tbody": { display: "block" },
                      "& thead": {
                        position: "absolute",
                        width: 1,
                        height: 1,
                        overflow: "hidden",
                        clipPath: "inset(50%)",
                        whiteSpace: "nowrap",
                      },
                      "& tbody tr": { display: "block", p: 1.5 },
                      "& tbody tr + tr": {
                        borderTop: 1,
                        borderColor: "divider",
                      },
                      "& tbody th, & tbody td": {
                        display: "block",
                        borderBottom: 0,
                        px: 0,
                        py: 0.5,
                      },
                      "& tbody td[data-label]": {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                      },
                      "& tbody td[data-label]::before": {
                        content: "attr(data-label)",
                        color: "text.secondary",
                        flexShrink: 0,
                      },
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: "34%" }}>Name / email</TableCell>
                      <TableCell sx={{ width: "18%" }}>Access</TableCell>
                      <TableCell sx={{ width: "17%" }}>Status</TableCell>
                      <TableCell sx={{ width: "15%" }}>Billing</TableCell>
                      <TableCell align='right' sx={{ width: "16%" }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {members.owner && (
                      <MemberRow member={members.owner} owner />
                    )}
                    {subUsers.map((member) => (
                      <MemberRow
                        key={member.u_id}
                        member={member}
                        action={
                          <Button
                            size='small'
                            variant='outlined'
                            color='error'
                            aria-label={`Remove ${member.name || member.email}`}
                            disabled={changesDisabled || !reasonReady}
                            onClick={() => removeSubUser(member)}
                          >
                            Remove
                          </Button>
                        }
                      />
                    ))}
                    {subUsers.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          sx={{ color: "text.secondary", textAlign: "center" }}
                        >
                          No sub-users yet. Add a user using the form.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {!members.owner && (
                <Typography variant='body2' color='text.secondary'>
                  No owner assigned.
                </Typography>
              )}
              <Note title='How this affects the bill'>
                <Stack spacing={0.75}>
                  <Typography variant='body2' color='text.secondary'>
                    Unpaid renewal bills update when you add or remove a user.
                    Blocked users are not charged.
                  </Typography>
                  {openRenewal && (
                    <>
                      <Typography variant='body2' fontWeight={600}>
                        Unpaid renewal bill:{" "}
                        {formatMoney(openRenewal.charge_after_discount)} (before
                        changes)
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {openRenewal.cycle_started
                          ? "This bill covers the current billing period. Users on this bill are not charged separately for the same period."
                          : "This bill covers the next billing period. Adding a user may also add a charge for the rest of this period."}
                      </Typography>
                    </>
                  )}
                </Stack>
              </Note>
              {(members.open_renewals || []).length > 1 && (
                <Note title='Check the renewal bills first'>
                  <Typography variant='body2' color='text.secondary'>
                    There is more than one unpaid renewal bill. Close this
                    window and choose “Prepare renewal” before changing users.
                  </Typography>
                </Note>
              )}
            </>
          )}
        </Stack>

        <Stack
          component='section'
          aria-labelledby={`${formId}-add`}
          spacing={2}
          sx={{
            minWidth: 0,
            p: 2.5,
            bgcolor: "action.hover",
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            "& .MuiOutlinedInput-root": { bgcolor: "background.paper" },
          }}
        >
          <Box>
            <Typography
              id={`${formId}-add`}
              component='h3'
              variant='subtitle2'
              fontWeight={700}
            >
              Add a user
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
              We’ll send them an email invitation.
            </Typography>
          </Box>
          <Stack spacing={1}>
            <Typography
              component='label'
              htmlFor={`${formId}-name`}
              variant='body2'
              fontWeight={600}
            >
              Full name
            </Typography>
            <TextField
              id={`${formId}-name`}
              size='small'
              required
              placeholder='Enter their name'
              value={name}
              onChange={(event) => setName(event.target.value)}
              disabled={updating}
              fullWidth
            />
          </Stack>
          <Stack spacing={1}>
            <Typography
              component='label'
              htmlFor={`${formId}-email`}
              variant='body2'
              fontWeight={600}
            >
              Email address
            </Typography>
            <TextField
              id={`${formId}-email`}
              size='small'
              type='email'
              required
              placeholder='name@company.com'
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={updating}
              fullWidth
            />
          </Stack>
          <Stack spacing={1}>
            <Typography
              id={`${formId}-access-label`}
              component='label'
              htmlFor={`${formId}-access`}
              variant='body2'
              fontWeight={600}
            >
              Access level
            </Typography>
            <TextField
              id={`${formId}-access`}
              select
              size='small'
              value={role}
              onChange={(event) => setRole(event.target.value)}
              disabled={updating}
              SelectProps={{ labelId: `${formId}-access-label` }}
              fullWidth
            >
              {ROLES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <Stack spacing={1}>
            <Typography
              component='label'
              htmlFor={`${formId}-reason`}
              variant='body2'
              fontWeight={600}
            >
              Reason for this change
            </Typography>
            <TextField
              id={`${formId}-reason`}
              size='small'
              required
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              disabled={updating}
              placeholder='A colleague has joined or left'
              fullWidth
            />
          </Stack>
        </Stack>
      </Box>

      <SubscriptionActionFooter>
        <Button
          variant='contained'
          disableElevation
          onClick={addSubUser}
          disabled={
            changesDisabled || !name.trim() || !email.trim() || !reasonReady
          }
          startIcon={
            updating ? (
              <CircularProgress size={16} color='inherit' />
            ) : (
              <PersonAddAltOutlinedIcon fontSize='small' />
            )
          }
          sx={{ textTransform: "none" }}
        >
          {updating ? "Saving..." : "Send invitation"}
        </Button>
      </SubscriptionActionFooter>
    </Stack>
  );
}
